// ModalEditGiftcardAdmin.jsx
import React from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button } from 'react-bootstrap';
import './ModalEditGiftcard.Module.css';
import toast from 'react-hot-toast';
import ModalConfirmation from '../modalConfirmation/ModalConfirmation';
import axios from 'axios';
import { formatDate } from '../../../utils/validate/validate';
import { currencyDisplay } from '../../../utils/cart/cart';

const ModalEditGiftcardAdmin = ({ show, handleClose, giftcard }) => {
  const user = useSelector((state) => state.user.userInfo);
  const isSuperAdmin = user?.role === 'SUPERADMIN_ROLE';

  const token = localStorage.getItem('Token');
  const config = {
    headers: {
      xtoken: `${token}`,
    },
  };

  const estadosGift = ['DISPONIBLE', 'UTILIZADA', 'VENCIDA'];

  // Helper para el input date (YYYY-MM-DD)
  const toDateInputValue = (dateLike) => {
    if (!dateLike) return '';
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return '';
    // date input necesita YYYY-MM-DD
    return d.toISOString().slice(0, 10);
  };

  // Estado del form (editable)
  const [formData, setFormData] = React.useState({
    estado: giftcard?.estado ?? 'DISPONIBLE',
    precio: Number(giftcard?.precio ?? 0),
    saldoConsumido: Number(giftcard?.saldoConsumido ?? 0),
    fechaVencimiento: toDateInputValue(giftcard?.vencimiento),
  });

  // Si abrís el modal con otra giftcard, resetea el form
  React.useEffect(() => {
    if (!giftcard) return;
    setFormData({
      estado: giftcard?.estado ?? 'DISPONIBLE',
      precio: Number(giftcard?.precio ?? 0),
      saldoConsumido: Number(giftcard?.saldoConsumido ?? 0),
      fechaVencimiento: toDateInputValue(giftcard?.vencimiento),
    });
  }, [giftcard]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ['precio', 'saldoConsumido'];

    setFormData((prev) => ({
      ...prev,
      [name]: numericFields.includes(name)
        ? (value === '' ? 0 : Number(value))
        : value,
    }));
  };

  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const handleShowConfirmation = () => setShowConfirmation(true);

  const handleCancelDeleteGiftcard = () => setShowConfirmation(false);

  const handleDeleteGiftcard = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACK_URL}/api/giftcards/${giftcard?._id}`,
        config
      );

      window.dispatchEvent(new Event('actualizar-giftcard'));
      toast.success('Giftcard eliminada con éxito');
      setShowConfirmation(false);
      handleClose();
    } catch (error) {
      toast.error('Error al eliminar la giftcard');
      setShowConfirmation(false);
    }
  };

  const handleModalSave = async () => {
    try {
      const saldoDisponible = formData.precio - formData.saldoConsumido;

      const data = {
        estado: formData.estado,
        precio: formData.precio,
        saldoConsumido: formData.saldoConsumido,
        saldoDisponible,
      };

      if (isSuperAdmin) {
        // mandamos YYYY-MM-DD; el back puede parsearlo a Date
        data.fechaVencimiento = formData.fechaVencimiento;
      }

      await axios.put(
        `${process.env.REACT_APP_BACK_URL}/api/giftcards/admin/${giftcard?.codigo}`,
        data,
        config
      );

      window.dispatchEvent(new Event('actualizar-giftcard'));
      toast.success('Giftcard actualizada con éxito');
      handleClose();
    } catch (error) {
      // console.log(error);
      toast.error('Ocurrió un error al actualizar la giftcard');
    }
  };

  const saldoDisponibleCalc = formData.precio - formData.saldoConsumido;

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header className='headerModalHistorial' closeButton>
        <Modal.Title>Editar giftcard</Modal.Title>
      </Modal.Header>

      <Modal.Body className='modalConfirmationContainer'>
        <ModalConfirmation
          className='modaaaaal'
          show={showConfirmation}
          title='Eliminar giftcard'
          message='¿Estás seguro de que deseas eliminar la Giftcard?'
          onConfirm={handleDeleteGiftcard}
          onCancel={handleCancelDeleteGiftcard}
        />

        <div>
          <label>Cliente:</label>
          <h5>
            {giftcard?.cliente?.nombre} {giftcard?.cliente?.apellido}
          </h5>
        </div>

        <div>
          <label>Código:</label>
          <h5>{giftcard?.codigo}</h5>
        </div>

        <div>
          <label>Fecha de emisión:</label>
          <h5>{formatDate(giftcard?.fecha)}</h5>
        </div>

        <div>
          <label>Fecha de vencimiento actual:</label>
          <h5>{formatDate(giftcard?.vencimiento)}</h5>
        </div>

        {/* EDITABLE: Saldo inicial (precio) */}
        <div className='giftcardInput'>
          <label htmlFor='precio'>Saldo inicial:</label>
          <input
            type='number'
            id='precio'
            name='precio'
            value={formData.precio}
            onChange={handleChange}
            min='0'
          />
          <small className='text-muted'>
            (Referencia visual: {currencyDisplay(formData.precio)})
          </small>
        </div>

        {/* EDITABLE: Saldo consumido */}
        <div className='giftcardInput'>
          <label htmlFor='saldoConsumido'>Saldo consumido:</label>
          <input
            type='number'
            id='saldoConsumido'
            name='saldoConsumido'
            value={formData.saldoConsumido}
            onChange={handleChange}
            min='0'
          />
          <small className='text-muted'>
            (Referencia visual: {currencyDisplay(formData.saldoConsumido)})
          </small>
        </div>

        {/* CALCULADO: Saldo disponible */}
        <div>
          <label>Saldo disponible:</label>
          <h5>{currencyDisplay(saldoDisponibleCalc)}</h5>
        </div>

        {/* EDITABLE (solo superadmin): Fecha de vencimiento */}
        {isSuperAdmin && (
          <div className='giftcardInput'>
            <label htmlFor='fechaVencimiento'>Modificar fecha de vencimiento:</label>
            <input
              type='date'
              id='fechaVencimiento'
              name='fechaVencimiento'
              value={formData.fechaVencimiento}
              onChange={handleChange}
            />
          </div>
        )}

        {/* EDITABLE: Estado */}
        <div className='giftcardInput'>
          <label htmlFor='estado'>Estado:</label>
          <select
            id='estado'
            name='estado'
            value={formData.estado}
            onChange={handleChange}
          >
            {estadosGift.map((estado) => (
              <option key={estado} value={estado}>
                {estado}
              </option>
            ))}
          </select>
        </div>

        <Button
          className='danger-edit'
          variant='danger'
          onClick={handleShowConfirmation}
        >
          Eliminar giftcard
        </Button>

        <div className='divMagicoReset py-2 mt-4'>
          <Button variant='secondary' onClick={handleClose}>
            Cerrar
          </Button>
          <Button
            className='botonModalConfirmacion'
            variant='primary'
            onClick={handleModalSave}
            disabled={saldoDisponibleCalc < 0}
            title={saldoDisponibleCalc < 0 ? 'El saldo consumido no puede superar el saldo inicial' : ''}
          >
            Guardar Cambios
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ModalEditGiftcardAdmin;
